import type { Authentication, AuthMechanism, Credentials, LoginFailure } from '@ekkogmbh/apisdk';

// =========================================================
// The E2E library as this mechanism relies on it
// =========================================================
//
// `public/auth/e2e.js` is customer-maintained and stays untouched, so the
// mechanism adapts to it:
//
// - The login page loads the library and hands it over with `setE2E()`.
// - The library is one object for the whole page. Its first `init()`
//   registers the status handler for good; every later `init()` only reports
//   status 0 to that handler. Later logins therefore go through `fetchToken()`.
// - With WebEAM the provider's answer comes back in the URL fragment, and the
//   library reports it while `init()` or `fetchToken()` still runs.
// - `fetchToken()` must get neither credentials nor a callback: credentials,
//   even empty ones, make the library answer a provider error by asking for a
//   token again, without end (EKKO-2095), and a callback becomes a second
//   handler that receives the next status as well.
// - The library ignores the promise the handler returns.

type LoginCallback = (failure?: LoginFailure) => Promise<void>;

interface E2EStatus {
  code: number;
  message?: unknown;
}

/** A WebEAM login that ended without a token. */
export class WebEamLoginError extends Error {
  public readonly cause?: unknown;

  constructor(message: string, options: { cause?: unknown } = {}) {
    super(message);
    this.name = 'WebEamLoginError';
    this.cause = options.cause;
  }
}

// The handler registered with the library's one `init()` forwards every status
// to the mechanism that started the latest login, also when that is another
// mechanism on the same library.
interface StatusDispatcher {
  target: WebEamAuthMechanism;
}

const dispatchers = new WeakMap<E2E, StatusDispatcher>();

// Used when `authenticate()` gets no callback, so that a failure still reaches
// the caller of `authenticate()`.
const rejectFailure: LoginCallback = async (failure) => {
  if (failure !== undefined) {
    throw failure.cause;
  }
};

// The library reads the provider's answer from the fragment on every login.
// A token left there would be used again by the next page load, also once it
// has expired, and a left-over error would fail it. The route does not depend
// on the fragment, and keeping the entry's state keeps the Next router's.
const clearProviderAnswer = (): void => {
  if (window.location.hash === '') {
    return;
  }

  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
};

// Status 3 carries the decoded fragment. The library turns only the first '+'
// of the description into a space, while providers form-encode every space.
const providerErrorOf = (message: unknown): WebEamLoginError => {
  const answer: { error?: unknown; error_description?: unknown } =
    typeof message === 'object' && message !== null ? message : {};
  const reason = [answer.error_description, answer.error].find(
    (text): text is string => typeof text === 'string' && text !== '',
  );

  return new WebEamLoginError(
    reason !== undefined ? reason.replace(/\+/g, ' ') : 'The login service rejected the login.',
  );
};

export class WebEamAuthMechanism implements AuthMechanism {
  public authentication: Authentication = { isAuthenticated: false };
  private callback: LoginCallback = rejectFailure;

  // While `authenticate()` runs, the session's completion of a status reported
  // meanwhile is kept here, so that `authenticate()` settles with it.
  private isStarting: boolean = false;
  private startCompletion?: Promise<void>;

  constructor(
    private e2e: E2E | null,
    private readonly e2eSettings: E2ESettings,
  ) {}

  public setE2E(e2e: E2E): void {
    this.e2e = e2e;
  }

  private getE2E(): E2E {
    if (this.e2e === null) {
      throw new Error('WebEamAuthMechanism: E2E not set.');
    }

    return this.e2e;
  }

  public async authenticate(
    _credentials?: Credentials,
    callback?: LoginCallback,
    _signal?: AbortSignal,
  ): Promise<void> {
    this.callback = callback ?? rejectFailure;
    this.startCompletion = undefined;
    this.isStarting = true;

    // The dispatcher points here before the library is called, as statuses
    // arrive while it runs. The library counts as initialised once `init()`
    // was called, also when it threw: it registered the handler before.
    try {
      const e2e = this.getE2E();
      const dispatcher = dispatchers.get(e2e);
      if (dispatcher === undefined) {
        const created: StatusDispatcher = { target: this };
        dispatchers.set(e2e, created);
        e2e.init(this.e2eSettings, (status: E2EStatus) => created.target.handleStatus(status));
      } else {
        dispatcher.target = this;
        e2e.fetchToken(undefined, undefined);
      }
    } catch (error) {
      // A fragment that is neither a token nor an error makes the library
      // recurse until the stack overflows, or fail to parse it.
      clearProviderAnswer();
      throw new WebEamLoginError('The login could not be completed.', { cause: error });
    } finally {
      this.isStarting = false;
    }

    // No status while the library ran (status 6 or a redirect to the
    // provider): the login goes on without this call, which resolves.
    await this.startCompletion;
  }

  // Revoking needs the library, which holds the token it acquired itself.
  public async invalidateAuthentication(): Promise<void> {
    this.authentication.authToken = undefined;
    this.authentication.user = undefined;
    this.authentication.isAuthenticated = false;

    this.getE2E().invalidateToken();
  }

  private async handleStatus(status: E2EStatus): Promise<void> {
    let failure: LoginFailure | undefined;

    switch (status.code) {
      case 1:
        // The session may have replaced `authentication` since the login
        // started; the token belongs into the object it holds now.
        this.authentication.authToken = { token: this.getE2E().token.access_token, refresh_token: '' };
        this.authentication.isAuthenticated = true;
        clearProviderAnswer();
        break;

      case 3:
        failure = { failed: true, cause: providerErrorOf(status.message) };
        clearProviderAnswer();
        break;

      // 2 and 4 come from the library's APISEC paths. Neither leads to a
      // token, and the fragment holds nothing of them.
      case 2:
        failure = { failed: true, cause: new WebEamLoginError('The login could not be completed.') };
        break;

      case 4:
        failure = { failed: true, cause: new WebEamLoginError('The login service cannot be reached.') };
        break;

      // 0 reports a configuration problem of the library, 5 follows a logout,
      // and 6 hands the login to a new window whose token arrives later as 1.
      // None of them ends the login.
      default:
        return;
    }

    let completion: Promise<void>;
    try {
      completion = failure === undefined ? this.callback() : this.callback(failure);
    } catch (error) {
      completion = Promise.reject(error);
    }

    if (this.isStarting) {
      this.startCompletion = completion;
    }

    // A completion that fails after `authenticate()` returned has no caller
    // left to reject, and the session has published the outcome already.
    await completion.catch(() => undefined);
  }
}
